"use client";

import { useEffect, useRef, useState, type ComponentType } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Plus,
  Edit,
  Trash2,
  Upload,
  FileText,
  Download,
  X,
  Users,
  Calculator,
  CheckSquare,
  ArrowRight,
  ChevronDown,
  CalendarDays,
  Heart,
  Cake,
  Baby,
  PartyPopper,
  Sparkles,
  Beer,
  Wine,
  LayoutList,
  CalendarClock,
} from "lucide-react";
import {
  EventDataLoading,
  OverviewStatsGridSkeleton,
} from "@/components/EventDataLoading";
import {
  getGuestLists,
  getBudget,
  getTodos,
} from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  fixSerbianText,
  drawPDFHeader,
  drawPDFFooter,
  loadPDFLogo,
  loadPDF,
  C,
  MARGIN,
  maxContentY,
} from "@/lib/pdf-utils";
import { usePlannerEvent } from "@/contexts/PlannerEventContext";
import type { PlannerEventType } from "@/lib/planner-events";
import { cn } from "@/lib/utils";

interface Entry {
  id: string;
  title: string; // Title shown in accordion header
  naziv: string;
  kontakt: string;
  cijena: string;
  dodatneInformacije: string;
}

interface TimelineEntry {
  id: string;
  title: string; // Title shown in accordion header
  vrijeme: string; // Start time in HH:mm format
  vrijemeEnd?: string; // End time in HH:mm format (optional, if present it's a time range)
  dodatneInformacije: string;
}

type EventType = PlannerEventType;

/** Edit / delete cluster on Organizacija & Raspored accordion rows. */
const accordionRowClassName = "flex w-full min-w-0 items-center";
const accordionRowActionsClassName =
  "flex shrink-0 items-center gap-2 pr-3 pl-1 sm:pr-4";

const plannerDialogHeaderClassName =
  "overflow-hidden rounded-t-lg bg-gradient-to-br from-muted/50 via-muted/30 to-transparent px-5 pb-3 pt-5 sm:px-6 sm:pt-6";

const EVENT_OPTIONS: Array<{
  type: EventType;
  label: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
}> = [
  {
    type: "svadba",
    label: "Svadba",
    description: "Planiranje venčanja i sve oko njega",
    icon: Heart,
  },
  {
    type: "rodjendan",
    label: "Rođendan",
    description: "Rođendanska proslava",
    icon: Cake,
  },
  {
    type: "krstenje",
    label: "Krštenje",
    description: "Krštenje i okupljanje",
    icon: Baby,
  },
  {
    type: "zurka",
    label: "Žurka",
    description: "Zabava i druženje",
    icon: PartyPopper,
  },
  {
    type: "momacko_vece",
    label: "Momačko veče",
    description: "Veče pred svadbu za mladoženju",
    icon: Beer,
  },
  {
    type: "djevojacko_vece",
    label: "Djevojačko veče",
    description: "Veče pred svadbu za mladu",
    icon: Wine,
  },
  {
    type: "custom",
    label: "Ostalo",
    description: "Prilagođen događaj po tvom ukusu",
    icon: Sparkles,
  },
];

function getSuggestedTitlesForEventType(type: EventType | null): string[] {
  switch (type) {
    case "rodjendan":
      return [
        "Torta",
        "Dekoracija",
        "Muzika / DJ",
        "Ketering",
        "Fotograf",
        "Zabava za djecu",
      ];
    case "krstenje":
      return [
        "Crkva / sveštenik",
        "Restoran / sala",
        "Ketering",
        "Fotograf",
        "Dekoracija",
        "Poklon za dijete",
      ];
    case "zurka":
      return [
        "Mjesto održavanja",
        "Muzika",
        "Piće i grickalice",
        "Dekoracija",
        "Osiguranje / red",
        "Fotograf / video",
        'Hrana i piće',
      ];
    case "momacko_vece":
      return [
        "Budžet i podjela troškova",
        "Lista gostiju",
        "Tip proslave (vibe)",
        "Lokacija i smještaj",
        "Hrana i piće",
        "Transport",
        "Muzika",
        "Personalizovani detalji",
        "Poklon za mladoženju",
        "Plan B",
      ];
    case "djevojacko_vece":
      return [
        "Budžet",
        "Tema i dekoracija",
        "Datum (timeline)",
        "Lokacija i format",
        "Catering i slatki sto",
        "Igre i aktivnosti",
        "Pokloni za goste",
        "Fotograf ili foto-kutak",
        "Dress code",
        "Planiranje iznenađenja",
      ];
    case "custom":
      return [
        "Lokacija",
        "Ketering",
        "Muzika",
        "Dekoracija",
        "Fotograf",
        "Dodatne usluge",
      ];
    case "svadba":
    default:
      return [
        "Muzika",
        "Fotograf",
        "Svadbena torta",
        "Dekoracija",
        "Specijalni efekti",
        "Ketering (kolači, voće)",
      ];
  }
}

export default function ProfilPage() {
  const { user, isLoading, logout, token } = useAuth();
  const router = useRouter();

  const {
    summaries: plannerSummaries,
    activeEventId,
    activeEventNumericId,
    setActiveEventId,
    activeSummary,
    record: plannerRecord,
    listReady: plannerListReady,
    detailLoading: plannerDetailLoading,
    setNeedsOnboarding,
    createEvent,
    deleteEvent,
    patchEvent,
  } = usePlannerEvent();

  const activeEventIdRef = useRef<string | null>(null);
  useEffect(() => {
    activeEventIdRef.current = activeEventId;
  }, [activeEventId]);

  const activeEventNumericIdRef = useRef(activeEventNumericId);
  useEffect(() => {
    activeEventNumericIdRef.current = activeEventNumericId;
  }, [activeEventNumericId]);

  const loadingSelections =
    !plannerListReady || (!!activeEventId && plannerDetailLoading);

  const [entries, setEntries] = useState<Entry[]>([]);

  // Overview data state
  const [overviewData, setOverviewData] = useState({
    totalGuests: 0,
    confirmedGuests: 0,
    pendingGuests: 0,
    cancelledGuests: 0,
    totalLists: 0,
    budgetTotal: 0,
    expensesTotal: 0,
    paidTotal: 0,
    totalTasks: 0,
    completedTasks: 0,
  });
  const [loadingOverview, setLoadingOverview] = useState(true);

  // Entries state
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [deletingEntryId, setDeletingEntryId] = useState<string | null>(null);
  const [editingEntryData, setEditingEntryData] = useState<Entry | null>(null);

  // Timeline entries state
  const [timelineEntries, setTimelineEntries] = useState<TimelineEntry[]>([]);
  const [editingTimelineEntryId, setEditingTimelineEntryId] = useState<
    string | null
  >(null);
  const [deletingTimelineEntryId, setDeletingTimelineEntryId] = useState<
    string | null
  >(null);
  const [editingTimelineEntryData, setEditingTimelineEntryData] =
    useState<TimelineEntry | null>(null);

  const [isEventTypeDialogOpen, setIsEventTypeDialogOpen] = useState(false);
  const [isEventNameDialogOpen, setIsEventNameDialogOpen] = useState(false);
  const [isMyEventsDialogOpen, setIsMyEventsDialogOpen] = useState(false);
  const [isRenameEventDialogOpen, setIsRenameEventDialogOpen] = useState(false);
  const [renameEventId, setRenameEventId] = useState<string | null>(null);
  const [renameEventName, setRenameEventName] = useState("");
  const [renameEventSaving, setRenameEventSaving] = useState(false);
  const [deleteConfirmEventId, setDeleteConfirmEventId] = useState<
    string | null
  >(null);
  const [deleteEventLoading, setDeleteEventLoading] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState<EventType | null>(
    null
  );
  const [newEventName, setNewEventName] = useState("");

  useEffect(() => {
    if (!isLoading && !user) {
      router.push("/prijava");
    }
  }, [user, isLoading, router]);

  const loadOverview = async () => {
    if (!user || !token) return;
    if (activeEventNumericId == null) {
      setOverviewData({
        totalGuests: 0,
        confirmedGuests: 0,
        pendingGuests: 0,
        cancelledGuests: 0,
        totalLists: 0,
        budgetTotal: 0,
        expensesTotal: 0,
        paidTotal: 0,
        totalTasks: 0,
        completedTasks: 0,
      });
      setLoadingOverview(false);
      return;
    }

    const forOverview = activeEventNumericIdRef.current;
    if (forOverview == null) return;

    try {
      setLoadingOverview(true);

      const guestLists = await getGuestLists(
        token,
        user.id,
        forOverview
      );
      if (activeEventNumericIdRef.current !== forOverview) return;

      const allGuests = guestLists.flatMap((list) => {
        const guests =
          list.attributes?.guests ||
          list.attributes?.guest ||
          list.guests ||
          list.guest ||
          [];
        return guests;
      });
      const totalGuests = allGuests.length;

      const confirmedGuests = allGuests.filter((g: any) => {
        const status = g.status || (g.confirmed ? "Potvrđen" : "Na čekanju");
        return status === "Potvrđen";
      }).length;

      const pendingGuests = allGuests.filter((g: any) => {
        const status = g.status || (g.confirmed ? "Potvrđen" : "Na čekanju");
        return status === "Na čekanju";
      }).length;

      const cancelledGuests = allGuests.filter((g: any) => {
        const status = g.status || (g.confirmed ? "Potvrđen" : "Na čekanju");
        return status === "Otkazan";
      }).length;

      const budget = await getBudget(token, user.id, forOverview);
      if (activeEventNumericIdRef.current !== forOverview) return;

      const budgetTotal = budget?.budget || 0;
      const expenses = budget?.expense || [];
      const expensesTotal = expenses.reduce(
        (sum: number, exp: any) =>
          sum + (parseFloat(exp.amount?.toString() || "0") || 0),
        0
      );
      const paidTotal = expenses
        .filter((exp: any) => exp.paid === true)
        .reduce(
          (sum: number, exp: any) =>
            sum + (parseFloat(exp.amount?.toString() || "0") || 0),
          0
        );

      const todos = await getTodos(token, user.id, forOverview);
      if (activeEventNumericIdRef.current !== forOverview) return;

      const totalTasks = todos.length;
      const completedTasks = todos.filter(
        (t) => t.completed || (t as any).attributes?.completed
      ).length;

      setOverviewData({
        totalGuests,
        confirmedGuests,
        pendingGuests,
        cancelledGuests,
        totalLists: guestLists.length,
        budgetTotal,
        expensesTotal,
        paidTotal,
        totalTasks,
        completedTasks,
      });
    } catch (err) {
      console.error("Error loading overview:", err);
    } finally {
      if (activeEventNumericIdRef.current === forOverview) {
        setLoadingOverview(false);
      }
    }
  };

  /** Sync organization UI from the active planner-event row (Strapi). */
  useEffect(() => {
    if (!user || !token || !plannerListReady) return;

    if (!activeEventId) {
      setEntries([]);
      setTimelineEntries([]);
      return;
    }

    if (plannerDetailLoading && !plannerRecord) {
      setEntries([]);
      setTimelineEntries([]);
      return;
    }

    if (!plannerRecord) return;

    if (plannerRecord.customEntries) {
      try {
        const loadedEntries =
          typeof plannerRecord.customEntries === "string"
            ? JSON.parse(plannerRecord.customEntries)
            : plannerRecord.customEntries;
        setEntries(Array.isArray(loadedEntries) ? loadedEntries : []);
      } catch (e) {
        console.error("Error parsing entries:", e);
        setEntries([]);
      }
    } else {
      setEntries([]);
    }

    if (plannerRecord.timelineEntries) {
      try {
        const loadedTimelineEntries =
          typeof plannerRecord.timelineEntries === "string"
            ? JSON.parse(plannerRecord.timelineEntries)
            : plannerRecord.timelineEntries;
        setTimelineEntries(
          Array.isArray(loadedTimelineEntries) ? loadedTimelineEntries : []
        );
      } catch (e) {
        console.error("Error parsing timeline entries:", e);
        setTimelineEntries([]);
      }
    } else {
      setTimelineEntries([]);
    }
  }, [
    user,
    token,
    plannerListReady,
    activeEventId,
    plannerRecord,
    plannerDetailLoading,
  ]);

  useEffect(() => {
    if (!user || !token || !plannerListReady) return;
    if (plannerSummaries.length === 0) {
      setOverviewData({
        totalGuests: 0,
        confirmedGuests: 0,
        pendingGuests: 0,
        cancelledGuests: 0,
        totalLists: 0,
        budgetTotal: 0,
        expensesTotal: 0,
        paidTotal: 0,
        totalTasks: 0,
        completedTasks: 0,
      });
      setLoadingOverview(false);
      return;
    }
    if (activeEventNumericId == null) {
      setOverviewData({
        totalGuests: 0,
        confirmedGuests: 0,
        pendingGuests: 0,
        cancelledGuests: 0,
        totalLists: 0,
        budgetTotal: 0,
        expensesTotal: 0,
        paidTotal: 0,
        totalTasks: 0,
        completedTasks: 0,
      });
      setLoadingOverview(false);
      return;
    }
    setLoadingOverview(true);
    loadOverview();
  }, [
    user,
    token,
    plannerListReady,
    activeEventNumericId,
    activeEventId,
    plannerSummaries.length,
  ]);

  useEffect(() => {
    if (!plannerListReady || plannerSummaries.length > 0) return;
    setIsMyEventsDialogOpen(false);
  }, [plannerListReady, plannerSummaries.length]);

  const saveEntries = async (entriesToSave: Entry[], forEventId: string) => {
    if (!token || !user || !forEventId) return;
    await patchEvent(forEventId, {
      customEntries: JSON.stringify(entriesToSave),
    });
  };

  const saveTimelineEntries = async (
    timelineEntriesToSave: TimelineEntry[],
    forEventId: string
  ) => {
    if (!token || !user || !forEventId) return;
    await patchEvent(forEventId, {
      timelineEntries: JSON.stringify(timelineEntriesToSave),
    });
  };

  const startEventCreation = (type: EventType) => {
    const opt = EVENT_OPTIONS.find((o) => o.type === type);
    setSelectedEventType(type);
    setNewEventName(opt?.label ?? "");
    setIsEventTypeDialogOpen(false);
    setIsEventNameDialogOpen(true);
  };

  const saveNewEvent = async () => {
    const type = selectedEventType;
    const name = newEventName.trim();
    if (!type || !name) return;
    try {
      await createEvent(name, type);
      setIsEventNameDialogOpen(false);
      setSelectedEventType(null);
      setNewEventName("");
      setNeedsOnboarding(false);
    } catch (err) {
      console.error(err);
      alert(
        "Greška pri kreiranju događaja. U Strapi: Authenticated → Planner Event → create."
      );
    }
  };

  const switchActiveEvent = (eventId: string) => {
    setActiveEventId(eventId);
    setIsMyEventsDialogOpen(false);
  };

  const openRenameEventDialog = (eventId: string) => {
    const ev = plannerSummaries.find((e) => e.id === eventId);
    if (!ev) return;
    setRenameEventId(eventId);
    setRenameEventName(ev.name);
    setIsRenameEventDialogOpen(true);
  };

  const saveRenamedEvent = async () => {
    const id = renameEventId;
    const name = renameEventName.trim();
    if (!id || !name || !token) return;
    setRenameEventSaving(true);
    try {
      await patchEvent(id, { name });
      setIsRenameEventDialogOpen(false);
      setRenameEventId(null);
      setRenameEventName("");
    } catch (err) {
      console.error(err);
      alert(
        "Greška pri čuvanju naziva. Proverite dozvole za Planner Event (update) u Strapi."
      );
    } finally {
      setRenameEventSaving(false);
    }
  };

  const confirmDeleteEvent = async () => {
    if (!deleteConfirmEventId) return;
    setDeleteEventLoading(true);
    try {
      await deleteEvent(deleteConfirmEventId);
      setDeleteConfirmEventId(null);
    } catch (err) {
      console.error(err);
      alert(
        "Greška pri brisanju proslave. Ako postoje povezani podaci (gosti, budžet, zadaci), prvo ih uklonite ili proverite dozvole za brisanje u Strapi."
      );
    } finally {
      setDeleteEventLoading(false);
    }
  };

  const handleAddEntry = () => {
    const newEntry: Entry = {
      id: Date.now().toString(),
      title: "",
      naziv: "",
      kontakt: "",
      cijena: "",
      dodatneInformacije: "",
    };
    setEditingEntryId(newEntry.id);
    setEditingEntryData(newEntry);
  };

  const handleAddSuggestedEntries = async () => {
    const forEventId = activeEventId;
    if (!forEventId) return;

    const suggestedTitles = getSuggestedTitlesForEventType(
      activeSummary?.eventType ?? "svadba"
    );

    const existingTitles = entries.map((e) => e.title);
    const newEntries = suggestedTitles
      .filter((title) => !existingTitles.includes(title))
      .map((title) => ({
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        title: title,
        naziv: "",
        kontakt: "",
        cijena: "",
        dodatneInformacije: "",
      }));

    if (newEntries.length === 0) {
      return;
    }

    try {
      const updatedEntries = [...entries, ...newEntries];
      await saveEntries(updatedEntries, forEventId);
      if (activeEventIdRef.current !== forEventId) return;
      setEntries(updatedEntries);
    } catch (err) {
      console.error("Error adding suggested entries:", err);
      alert(
        "Greška pri dodavanju predloženih stavki. Molimo pokušajte ponovo."
      );
    }
  };

  const shouldShowSuggestedButton = () => {
    const suggestedTitles = getSuggestedTitlesForEventType(
      activeSummary?.eventType ?? "svadba"
    );
    const existingTitles = entries.map((e) => e.title);
    return suggestedTitles.some((title) => !existingTitles.includes(title));
  };

  const handleEditEntry = (entry: Entry) => {
    setEditingEntryId(entry.id);
    setEditingEntryData({ ...entry });
  };

  const handleSaveEntry = async () => {
    if (!editingEntryData || !editingEntryData.title.trim()) {
      return;
    }

    const forEventId = activeEventId;
    if (!forEventId) return;

    try {
      const updatedEntries =
        editingEntryId && entries.find((e) => e.id === editingEntryId)
          ? entries.map((e) => (e.id === editingEntryId ? editingEntryData : e))
          : [...entries, editingEntryData];

      await saveEntries(updatedEntries, forEventId);
      if (activeEventIdRef.current !== forEventId) return;

      setEntries(updatedEntries);
      setEditingEntryId(null);
      setEditingEntryData(null);
    } catch (err) {
      console.error("Error saving entry:", err);
      alert("Greška pri čuvanju. Molimo pokušajte ponovo.");
    }
  };

  const handleDeleteEntry = async () => {
    if (!deletingEntryId) return;

    const forEventId = activeEventId;
    if (!forEventId) return;

    const updatedEntries = entries.filter((e) => e.id !== deletingEntryId);
    try {
      await saveEntries(updatedEntries, forEventId);
      if (activeEventIdRef.current !== forEventId) return;
      setEntries(updatedEntries);
      setDeletingEntryId(null);
    } catch (err) {
      console.error("Error deleting entry:", err);
      alert("Greška pri brisanju. Molimo pokušajte ponovo.");
    }
  };

  // Timeline entry handlers
  const handleAddTimelineEntry = () => {
    const newTimelineEntry: TimelineEntry = {
      id: Date.now().toString(),
      title: "",
      vrijeme: "",
      vrijemeEnd: undefined,
      dodatneInformacije: "",
    };
    setEditingTimelineEntryId(newTimelineEntry.id);
    setEditingTimelineEntryData(newTimelineEntry);
  };

  const handleEditTimelineEntry = (timelineEntry: TimelineEntry) => {
    setEditingTimelineEntryId(timelineEntry.id);
    setEditingTimelineEntryData({ ...timelineEntry });
  };

  const handleSaveTimelineEntry = async () => {
    if (!editingTimelineEntryData || !editingTimelineEntryData.title.trim()) {
      return;
    }

    const forEventId = activeEventId;
    if (!forEventId) return;

    try {
      const updatedTimelineEntries =
        editingTimelineEntryId &&
        timelineEntries.find((e) => e.id === editingTimelineEntryId)
          ? timelineEntries.map((e) =>
              e.id === editingTimelineEntryId ? editingTimelineEntryData : e
            )
          : [...timelineEntries, editingTimelineEntryData];

      await saveTimelineEntries(updatedTimelineEntries, forEventId);
      if (activeEventIdRef.current !== forEventId) return;

      setTimelineEntries(updatedTimelineEntries);
      setEditingTimelineEntryId(null);
      setEditingTimelineEntryData(null);
    } catch (err) {
      console.error("Error saving timeline entry:", err);
      alert("Greška pri čuvanju. Molimo pokušajte ponovo.");
    }
  };

  const handleDeleteTimelineEntry = async () => {
    if (!deletingTimelineEntryId) return;

    const forEventId = activeEventId;
    if (!forEventId) return;

    const updatedTimelineEntries = timelineEntries.filter(
      (e) => e.id !== deletingTimelineEntryId
    );
    try {
      await saveTimelineEntries(updatedTimelineEntries, forEventId);
      if (activeEventIdRef.current !== forEventId) return;
      setTimelineEntries(updatedTimelineEntries);
      setDeletingTimelineEntryId(null);
    } catch (err) {
      console.error("Error deleting timeline entry:", err);
      alert("Greška pri brisanju. Molimo pokušajte ponovo.");
    }
  };

  const exportTimelineToPDF = async () => {
    if (timelineEntries.length === 0) {
      alert("Nema stavki za izvoz");
      return;
    }

    try {
      const [{ jsPDF }] = await Promise.all([loadPDF(), loadPDFLogo()]);

      const doc = new jsPDF();
      const pw = doc.internal.pageSize.getWidth();

      const sorted = timelineEntries
        .filter((e) => e.vrijeme)
        .sort((a, b) => a.vrijeme.localeCompare(b.vrijeme));

      const date = new Date().toLocaleDateString("sr-RS");
      let pageNum = 1;

      // ── Layout constants ─────────────────────────────────────────────────
      const CARD_X     = MARGIN;                 // card left edge
      const CARD_W     = pw - MARGIN * 2;        // full usable width
      const ACCENT_W   = 3;                      // left accent strip width
      const PAD_L      = ACCENT_W + 8;           // text left padding inside card
      const PAD_V      = 6;                      // top/bottom padding inside card
      const TEXT_W     = CARD_W - PAD_L - 8;    // wrappable text width
      const CARD_GAP   = 5;                      // vertical gap between cards
      const TIME_LH    = 4.5;                    // time line height
      const TITLE_LH   = 5.5;                    // title line height
      const INFO_LH    = 4.5;                    // info line height
      const TIME_GAP   = 3;                      // gap between time and title
      const INFO_GAP   = 3;                      // gap between title and info

      /** Calculate card height for given content */
      const cardHeight = (
        titleLines: string[],
        infoLines: string[],
      ) => {
        let h = PAD_V + TIME_LH + TIME_GAP + titleLines.length * TITLE_LH + PAD_V;
        if (infoLines.length > 0) h += INFO_GAP + infoLines.length * INFO_LH;
        return h;
      };

      /** Draw header on fresh page */
      const startPage = () =>
        drawPDFHeader(doc, "Vremenski raspored", `Datum: ${date}`);

      startPage();

      if (sorted.length === 0) {
        doc.setFontSize(11);
        doc.setTextColor(...C.gray);
        doc.text(fixSerbianText("Nema stavki sa vremenom"), pw / 2, 60, { align: "center" });
        drawPDFFooter(doc, pageNum);
        doc.save(`Vremenski-raspored-${new Date().toISOString().split("T")[0]}.pdf`);
        return;
      }

      let y = 52;

      for (const entry of sorted) {
        const timeText = entry.vrijemeEnd
          ? `${entry.vrijeme} - ${entry.vrijemeEnd}`
          : entry.vrijeme;

        const titleLines = doc.splitTextToSize(
          fixSerbianText(entry.title || "Bez naziva"),
          TEXT_W,
        ) as string[];
        const infoLines = entry.dodatneInformacije
          ? (doc.splitTextToSize(fixSerbianText(entry.dodatneInformacije), TEXT_W) as string[])
          : [];

        const ch = cardHeight(titleLines, infoLines);

        // ── Page break ──────────────────────────────────────────────────────
        if (y + ch > maxContentY(doc)) {
          drawPDFFooter(doc, pageNum);
          pageNum++;
          doc.addPage();
          startPage();
          y = 52;
        }

        // ── Card background ─────────────────────────────────────────────────
        doc.setFillColor(248, 250, 255);         // near-white indigo tint
        doc.setDrawColor(...C.border);
        doc.setLineWidth(0.3);
        doc.roundedRect(CARD_X, y, CARD_W, ch, 2, 2, "FD");

        // ── Left accent strip ───────────────────────────────────────────────
        doc.setFillColor(...C.primary);
        doc.roundedRect(CARD_X, y, ACCENT_W, ch, 1, 1, "F");
        // cover the right corners of the strip so only left side is rounded
        doc.rect(CARD_X + 1, y, ACCENT_W - 1, ch, "F");

        // ── Time ────────────────────────────────────────────────────────────
        const textX = CARD_X + PAD_L;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(...C.primary);
        doc.text(timeText, textX, y + PAD_V + TIME_LH - 1);

        // ── Title ───────────────────────────────────────────────────────────
        const titleY = y + PAD_V + TIME_LH + TIME_GAP + TITLE_LH;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(...C.dark);
        doc.text(titleLines, textX, titleY);

        // ── Info text ────────────────────────────────────────────────────────
        if (infoLines.length > 0) {
          const infoY = titleY + (titleLines.length - 1) * TITLE_LH + INFO_GAP + INFO_LH;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(...C.gray);
          doc.text(infoLines, textX, infoY);
        }

        y += ch + CARD_GAP;
      }

      drawPDFFooter(doc, pageNum);
      doc.save(`Vremenski-raspored-${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("Error exporting timeline PDF:", error);
      alert("Greška pri konvertovanju u PDF");
    }
  };

  const plannerEventType = activeSummary?.eventType ?? "svadba";

  const organizacijaSubtitle = (() => {
    switch (plannerEventType) {
      case "svadba":
        return "Pregled stavki za svadbu: muzika, fotograf, sala ili vrt, ketering, dekoracija…";
      case "krstenje":
        return "Pregled stavki za krštenje: crkva, svečani ručak, fotograf, torta, dekoracija…";
      case "rodjendan":
        return "Pregled stavki za rođendan: torta, dekoracija, muzika, animacija, pokloni…";
      case "zurka":
        return "Pregled stavki za žurku: mjesto, muzika, piće, ketering, osiguranje…";
      case "momacko_vece":
        return "Pregled stavki za momačko veče: budžet, gosti, lokacija, transport, muzika…";
      case "djevojacko_vece":
        return "Pregled stavki za djevojačko veče: tema, lokacija, catering, igre, foto…";
      case "custom":
        return "Stavke koje sami birate za ovu proslavu — lista po vašim pravilima.";
      default:
        return "Pregled stavki za organizaciju događaja.";
    }
  })();

  const rasporedDogadjajaSubtitle = (() => {
    switch (plannerEventType) {
      case "svadba":
        return "Organizujte dan po satu: dolazak gostiju, vjenčanje, koktel, ručak, zabava…";
      case "krstenje":
        return "Raspored dana oko krštenja: obred u crkvi, slavlje, ručak, fotografisanje…";
      case "rodjendan":
        return "Planirajte rođendan po vremenu: dolazak, torta, igre, fotografisanje, završetak…";
      case "zurka":
        return "Postavite tok večeri: početak, muzika, grickalice, završetak…";
      case "momacko_vece":
        return "Raspored večeri: okupljanje, aktivnost ili lokal, večera, povratak ili smještaj…";
      case "djevojacko_vece":
        return "Raspored dana ili večeri: dolazak, aktivnosti, večera, foto, iznenađenje…";
      case "custom":
        return "Složite raspored kako vam odgovara — bilo koji redoslijed tačaka za vaš događaj.";
      default:
        return "Organizujte tačke događaja po vremenu.";
    }
  })();

  const organizacijaEmptyHint = (() => {
    switch (plannerEventType) {
      case "svadba":
        return "Dodajte prvu stavku (npr. fotograf ili muzika) klikom na dugme ispod.";
      case "krstenje":
        return "Dodajte prvu stavku (npr. crkva ili svečani ručak) klikom na dugme ispod.";
      case "rodjendan":
        return "Dodajte prvu stavku (npr. torta ili dekoracija) klikom na dugme ispod.";
      case "zurka":
        return "Dodajte prvu stavku (npr. mjesto ili muzika) klikom na dugme ispod.";
      case "momacko_vece":
        return "Dodajte prvu stavku (npr. budžet ili lokacija) klikom na dugme ispod.";
      case "djevojacko_vece":
        return "Dodajte prvu stavku (npr. tema ili lokacija) klikom na dugme ispod.";
      case "custom":
        return "Dodajte prvu stavku koja vam treba — klikom na dugme ispod.";
      default:
        return "Dodajte prvu stavku klikom na dugme ispod.";
    }
  })();

  const rasporedEmptyHint = (() => {
    switch (plannerEventType) {
      case "svadba":
        return "Dodajte prvi događaj u raspored (npr. vjenčanje ili ručak) klikom na dugme ispod.";
      case "krstenje":
        return "Dodajte prvi termin (npr. obred u crkvi ili ručak) klikom na dugme ispod.";
      case "rodjendan":
        return "Dodajte prvi termin (npr. dolazak gostiju ili torta) klikom na dugme ispod.";
      case "zurka":
        return "Dodajte prvi termin (npr. početak ili muzika) klikom na dugme ispod.";
      case "momacko_vece":
        return "Dodajte prvi termin (npr. okupljanje ili aktivnost) klikom na dugme ispod.";
      case "djevojacko_vece":
        return "Dodajte prvi termin (npr. dolazak ili aktivnost) klikom na dugme ispod.";
      case "custom":
        return "Dodajte prvu tačku u rasporedu klikom na dugme ispod.";
      default:
        return "Dodajte prvi termin klikom na dugme ispod.";
    }
  })();

  if (isLoading) {
    return (
      <EventDataLoading
        message="Učitavanje..."
        className="min-h-screen flex flex-col items-center justify-center gap-8 px-3 py-4 md:py-6 bg-gradient-to-br from-background to-muted"
      />
    );
  }

  if (!user) {
    return null;
  }

  const hasNoEvents = plannerListReady && plannerSummaries.length === 0;

  return (
    <div className="min-h-screen px-3 py-4 md:py-6 lg:py-8 pb-20 lg:pb-8">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="relative mb-8 overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-card via-card/95 to-muted/40 p-4 shadow-sm ring-1 ring-black/[0.03] dark:from-card/80 dark:to-muted/30 dark:ring-white/[0.06]">
          <div
            className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-primary/[0.07] blur-3xl dark:bg-primary/10"
            aria-hidden
          />
          <div className="relative flex flex-col gap-5 md:flex-row md:items-center md:justify-between md:gap-8">
            <div className="min-w-0">
              <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                Pozdrav,{" "}
                {(() => {
                  const firstName = user.ime
                    ? user.ime.split(" ")[0]
                    : user.username.split("_")[0].split(" ")[0];
                  return (
                    firstName.charAt(0).toUpperCase() +
                    firstName.slice(1).toLowerCase()
                  );
                })()}
              </h1>
            </div>
            {plannerListReady && activeSummary && (
              <Button
                type="button"
                variant="ghost"
                className="group h-auto w-full justify-between gap-3 rounded-2xl border border-border/80 bg-background/80 px-3 py-2.5 text-left shadow-md backdrop-blur-md transition-all hover:border-primary/25 hover:bg-background hover:shadow-lg dark:bg-background/50 sm:min-w-[16rem] sm:max-w-[min(100vw-2rem,22rem)] md:min-w-[18rem]"
                onClick={() => setIsMyEventsDialogOpen(true)}
              >
                <span className="flex min-w-0 flex-1 items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 text-primary shadow-inner ring-1 ring-primary/10 dark:text-white">
                    <CalendarDays className="h-5 w-5" strokeWidth={2} />
                  </span>
                  <span className="min-w-0 flex-1 truncate font-semibold tracking-tight">
                    {activeSummary.name}
                  </span>
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground opacity-70 transition-transform group-hover:translate-y-0.5" />
              </Button>
            )}
          </div>
        </div>

        {hasNoEvents ? (
          <Card className="border-dashed border-muted-foreground/20 bg-muted/15">
            <CardContent className="flex flex-col items-center justify-center px-6 py-14 text-center sm:py-20">
              <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/15 dark:text-white">
                <CalendarDays className="h-7 w-7" strokeWidth={1.5} />
              </div>
              <h2 className="text-xl font-semibold tracking-tight">
                Nemate nijednu proslavu
              </h2>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
                Kreirajte proslavu da biste koristili pregled gostiju, budžeta i
                zadataka, kao i organizaciju i raspored događaja.
              </p>
              <Button
                className="mt-8"
                size="lg"
                onClick={() => setIsEventTypeDialogOpen(true)}
              >
                <Plus className="mr-2 h-5 w-5" />
                Nova proslava
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Overview Cards */}
            {/* Mobile: Accordion (<768px) */}
            <div className="md:hidden mb-2">
              <Accordion
                type="single"
                collapsible
                defaultValue="gosti"
                className="w-full space-y-2"
              >
                {/* Guests Overview */}
                <AccordionItem
                  value="gosti"
                  className="border rounded-lg bg-card"
                >
                  <AccordionHeader>
                    <AccordionTrigger className="hover:no-underline !px-4 !py-4">
                      <div className="flex items-center justify-between w-full pr-4">
                        <div className="flex items-center gap-2">
                          <Users className="h-5 w-5 text-primary dark:text-white" />
                          <span className="font-semibold text-base">Gosti</span>
                        </div>
                      </div>
                    </AccordionTrigger>
                  </AccordionHeader>
                  <AccordionContent className="!px-4 !pb-4">
                    <div className="space-y-4">
                      {loadingOverview ? (
                        <OverviewStatsGridSkeleton cells={4} />
                      ) : (
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <p className="text-2xl font-semibold">
                                {overviewData.totalGuests > 0
                                  ? overviewData.totalGuests
                                  : "-"}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Ukupno gostiju
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-2xl font-semibold text-green-600 dark:text-green-500">
                                {overviewData.confirmedGuests > 0
                                  ? overviewData.confirmedGuests
                                  : "-"}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Potvrđenih
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-2xl font-semibold text-orange-600 dark:text-orange-400">
                                {overviewData.pendingGuests > 0
                                  ? overviewData.pendingGuests
                                  : "-"}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Na čekanju
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-2xl font-semibold text-red-600 dark:text-red-400">
                                {overviewData.cancelledGuests > 0
                                  ? overviewData.cancelledGuests
                                  : "-"}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Otkazanih
                              </p>
                            </div>
                          </div>
                        </>
                      )}
                      <Button
                        className="w-full mt-4"
                        onClick={() => router.push("/spisak-gostiju")}
                      >
                        Pregledaj sve <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Budget Overview */}
                <AccordionItem
                  value="budzet"
                  className="border rounded-lg bg-card"
                >
                  <AccordionHeader>
                    <AccordionTrigger className="hover:no-underline !px-4 !py-4">
                      <div className="flex items-center justify-between w-full pr-4">
                        <div className="flex items-center gap-2">
                          <Calculator className="h-5 w-5 text-primary dark:text-white" />
                          <span className="font-semibold text-base">
                            Budžet
                          </span>
                        </div>
                      </div>
                    </AccordionTrigger>
                  </AccordionHeader>
                  <AccordionContent className="!px-4 !pb-4">
                    <div className="space-y-4">
                      {loadingOverview ? (
                        <OverviewStatsGridSkeleton cells={4} />
                      ) : (
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <p className="text-2xl font-semibold">
                                {overviewData.budgetTotal > 0
                                  ? `${overviewData.budgetTotal.toLocaleString(
                                      "sr-RS"
                                    )} KM`
                                  : "-"}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Ukupan budžet
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-2xl font-semibold text-orange-600 dark:text-orange-400">
                                {overviewData.expensesTotal > 0
                                  ? `${overviewData.expensesTotal.toLocaleString(
                                      "sr-RS"
                                    )} KM`
                                  : "-"}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Potrošeno
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-2xl font-semibold text-green-600 dark:text-green-500">
                                {overviewData.paidTotal > 0
                                  ? `${overviewData.paidTotal.toLocaleString(
                                      "sr-RS"
                                    )} KM`
                                  : "-"}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Plaćeno
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p
                                className={`text-2xl font-semibold ${
                                  overviewData.budgetTotal -
                                    overviewData.expensesTotal <
                                  0
                                    ? "text-red-600 dark:text-red-400"
                                    : overviewData.budgetTotal -
                                        overviewData.expensesTotal ===
                                      0
                                    ? "text-foreground"
                                    : "text-green-600 dark:text-green-500"
                                }`}
                              >
                                {overviewData.budgetTotal -
                                  overviewData.expensesTotal !==
                                0
                                  ? `${(
                                      overviewData.budgetTotal -
                                      overviewData.expensesTotal
                                    ).toLocaleString("sr-RS")} KM`
                                  : "-"}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Preostalo
                              </p>
                            </div>
                          </div>
                        </>
                      )}
                      <Button
                        className="w-full mt-4"
                        onClick={() => router.push("/kalukator-budzeta")}
                      >
                        Pregledaj sve <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Tasks Overview */}
                <AccordionItem
                  value="zadaci"
                  className="border rounded-lg bg-card"
                >
                  <AccordionHeader>
                    <AccordionTrigger className="hover:no-underline !px-4 !py-4">
                      <div className="flex items-center justify-between w-full pr-4">
                        <div className="flex items-center gap-2">
                          <CheckSquare className="h-5 w-5 text-primary dark:text-white" />
                          <span className="font-semibold text-base">
                            Zadaci
                          </span>
                        </div>
                      </div>
                    </AccordionTrigger>
                  </AccordionHeader>
                  <AccordionContent className="!px-4 !pb-4">
                    <div className="space-y-4">
                      {loadingOverview ? (
                        <OverviewStatsGridSkeleton cells={3} />
                      ) : (
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <p className="text-2xl font-semibold">
                                {overviewData.totalTasks > 0
                                  ? overviewData.totalTasks
                                  : "-"}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Ukupno zadataka
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-2xl font-semibold text-green-600 dark:text-green-500">
                                {overviewData.completedTasks > 0
                                  ? overviewData.completedTasks
                                  : "-"}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Završenih
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-2xl font-semibold text-red-600 dark:text-red-400">
                                {overviewData.totalTasks -
                                  overviewData.completedTasks >
                                0
                                  ? overviewData.totalTasks -
                                    overviewData.completedTasks
                                  : "-"}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Preostalo
                              </p>
                            </div>
                          </div>
                        </>
                      )}
                      <Button
                        className="w-full mt-4"
                        onClick={() => router.push("/zadaci")}
                      >
                        Pregledaj sve <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            {/* Desktop: Grid Layout (>=768px) */}
            <div className="hidden md:grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-6">
              {/* Guests Overview */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Gosti</CardTitle>
                    <Users className="h-5 w-5 text-primary dark:text-white" />
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {loadingOverview ? (
                    <OverviewStatsGridSkeleton cells={4} />
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <p className="text-2xl font-semibold">
                            {overviewData.totalGuests > 0
                              ? overviewData.totalGuests
                              : "-"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Ukupno gostiju
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-2xl font-semibold text-green-600 dark:text-green-500">
                            {overviewData.confirmedGuests > 0
                              ? overviewData.confirmedGuests
                              : "-"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Potvrđenih
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-2xl font-semibold text-orange-600 dark:text-orange-400">
                            {overviewData.pendingGuests > 0
                              ? overviewData.pendingGuests
                              : "-"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Na čekanju
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-2xl font-semibold text-red-600 dark:text-red-400">
                            {overviewData.cancelledGuests > 0
                              ? overviewData.cancelledGuests
                              : "-"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Otkazanih
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                  <Button
                    className="w-full mt-4"
                    onClick={() => router.push("/spisak-gostiju")}
                  >
                    Pregledaj sve <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>

              {/* Budget Overview */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Budžet</CardTitle>
                    <Calculator className="h-5 w-5 text-primary dark:text-white" />
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {loadingOverview ? (
                    <OverviewStatsGridSkeleton cells={4} />
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <p className="text-2xl font-semibold">
                            {overviewData.budgetTotal > 0
                              ? `${overviewData.budgetTotal.toLocaleString(
                                  "sr-RS"
                                )} KM`
                              : "-"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Ukupan budžet
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-2xl font-semibold text-orange-600 dark:text-orange-400">
                            {overviewData.expensesTotal > 0
                              ? `${overviewData.expensesTotal.toLocaleString(
                                  "sr-RS"
                                )} KM`
                              : "-"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Potrošeno
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-2xl font-semibold text-green-600 dark:text-green-500">
                            {overviewData.paidTotal > 0
                              ? `${overviewData.paidTotal.toLocaleString(
                                  "sr-RS"
                                )} KM`
                              : "-"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Plaćeno
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p
                            className={`text-2xl font-semibold ${
                              overviewData.budgetTotal -
                                overviewData.expensesTotal <
                              0
                                ? "text-red-600 dark:text-red-400"
                                : overviewData.budgetTotal -
                                    overviewData.expensesTotal ===
                                  0
                                ? "text-foreground"
                                : "text-green-600 dark:text-green-500"
                            }`}
                          >
                            {overviewData.budgetTotal -
                              overviewData.expensesTotal !==
                            0
                              ? `${(
                                  overviewData.budgetTotal -
                                  overviewData.expensesTotal
                                ).toLocaleString("sr-RS")} KM`
                              : "-"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Preostalo
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                  <Button
                    className="w-full mt-4"
                    onClick={() => router.push("/kalukator-budzeta")}
                  >
                    Pregledaj sve <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>

              {/* Tasks Overview */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Zadaci</CardTitle>
                    <CheckSquare className="h-5 w-5 text-primary dark:text-white" />
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {loadingOverview ? (
                    <OverviewStatsGridSkeleton cells={3} />
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <p className="text-2xl font-semibold">
                            {overviewData.totalTasks > 0
                              ? overviewData.totalTasks
                              : "-"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Ukupno zadataka
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-2xl font-semibold text-green-600 dark:text-green-500">
                            {overviewData.completedTasks > 0
                              ? overviewData.completedTasks
                              : "-"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Završenih
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-2xl font-semibold text-red-600 dark:text-red-400">
                            {overviewData.totalTasks -
                              overviewData.completedTasks >
                            0
                              ? overviewData.totalTasks -
                                overviewData.completedTasks
                              : "-"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Preostalo
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                  <Button
                    className="w-full mt-4"
                    onClick={() => router.push("/zadaci")}
                  >
                    Pregledaj sve <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Content */}
            <div className="space-y-2">
              {/* Entries Section */}
              <div className="space-y-2 mt-6 md:mt-0">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="space-y-1">
                    <h2 className="text-lg font-semibold">Organizacija</h2>
                    <p className="text-sm text-muted-foreground">
                      {organizacijaSubtitle}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0 ml-auto w-fit justify-end mb-4">
                    {shouldShowSuggestedButton() && (
                      <Button
                        onClick={handleAddSuggestedEntries}
                        variant="outline"
                        className="w-fit"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Dodaj predloženo
                      </Button>
                    )}
                    <Button
                      onClick={handleAddEntry}
                      variant="default"
                      className="w-fit"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Dodaj
                    </Button>
                  </div>
                </div>
                {entries.length > 0 ? (
                  <>
                    <Accordion
                      type="multiple"
                      className="w-full space-y-3 mt-2"
                    >
                      {entries.map((entry) => (
                        <AccordionItem
                          key={entry.id}
                          value={entry.id}
                          className="border rounded-lg bg-card shadow-sm md:hover:shadow-lg md:hover:scale-[1.01] dark:md:hover:bg-muted/50 md:hover:border-primary/20 transition-all duration-200 ease-in-out group overflow-hidden"
                        >
                          <div className={accordionRowClassName}>
                            <AccordionHeader className="min-w-0 flex-1">
                              <AccordionTrigger className="hover:no-underline !p-4">
                                <span className="min-w-0 flex-1 text-left text-base font-semibold text-foreground">
                                  {entry.title || "Bez naziva"}
                                </span>
                              </AccordionTrigger>
                            </AccordionHeader>
                            <div className={accordionRowActionsClassName}>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 hover:bg-muted"
                                onClick={() => handleEditEntry(entry)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 text-destructive hover:bg-destructive/10"
                                onClick={() => setDeletingEntryId(entry.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          <AccordionContent className="!px-5 !pb-5">
                            <div className="space-y-5 pt-2">
                              <div className="space-y-1.5 pb-3 border-b">
                                <Label
                                  className="text-sm font-medium text-muted-foreground"
                                  style={{ fontSize: "14px" }}
                                >
                                  Naziv
                                </Label>
                                <p className="text-sm font-medium text-foreground">
                                  {entry.naziv || "-"}
                                </p>
                              </div>
                              <div className="space-y-1.5 pb-3 border-b">
                                <Label
                                  className="text-sm font-medium text-muted-foreground"
                                  style={{ fontSize: "14px" }}
                                >
                                  Kontakt
                                </Label>
                                <p className="text-sm text-foreground">
                                  {entry.kontakt || "-"}
                                </p>
                              </div>
                              <div className="space-y-1.5 pb-3 border-b">
                                <Label
                                  className="text-sm font-medium text-muted-foreground"
                                  style={{ fontSize: "14px" }}
                                >
                                  Cijena
                                </Label>
                                <p className="text-sm text-foreground">
                                  {entry.cijena || "-"}
                                </p>
                              </div>
                              <div className="space-y-1.5">
                                <Label
                                  className="text-sm font-medium text-muted-foreground"
                                  style={{ fontSize: "14px" }}
                                >
                                  Dodatne informacije
                                </Label>
                                <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                                  {entry.dodatneInformacije || "-"}
                                </div>
                              </div>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                    <div className="flex justify-center pt-3">
                      <Button
                        onClick={handleAddEntry}
                        variant="default"
                        className="w-10 h-10 p-0"
                      >
                        <Plus className="h-5 w-5" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="rounded-xl border border-dashed border-muted-foreground/20 bg-muted/25 px-5 py-10 text-center sm:px-8">
                      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/15 dark:text-white">
                        <LayoutList className="h-6 w-6" strokeWidth={1.75} />
                      </div>
                      <p className="text-sm font-semibold tracking-tight text-foreground">
                        Nema dodatih stavki u organizaciji
                      </p>
                      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
                        {organizacijaEmptyHint}
                      </p>
                    </div>
                    <div className="flex justify-center pt-2">
                      <Button onClick={handleAddEntry} className="w-10 h-10">
                        <Plus className="h-5 w-5" />
                      </Button>
                    </div>
                  </>
                )}
              </div>

              {/* Timeline Section */}
              <div className="space-y-2 mt-6">
                <div className="flex flex-col items-end sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="space-y-1">
                    <h2 className="text-lg font-semibold">Raspored događaja</h2>
                    <p className="text-sm text-muted-foreground">
                      {rasporedDogadjajaSubtitle}
                    </p>
                  </div>
                  <div className="flex gap-2 w-fit ml-auto sm:w-auto mb-4">
                    {timelineEntries.length > 0 && (
                      <Button
                        onClick={exportTimelineToPDF}
                        variant="outline"
                        className="w-fit"
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Konvertuj u PDF
                      </Button>
                    )}
                    <Button
                      onClick={handleAddTimelineEntry}
                      variant="default"
                      className="w-fit"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Dodaj
                    </Button>
                  </div>
                </div>
                {timelineEntries.length > 0 ? (
                  <>
                    <Accordion
                      type="multiple"
                      className="w-full space-y-3 mt-2"
                    >
                      {timelineEntries.map((timelineEntry) => (
                        <AccordionItem
                          key={timelineEntry.id}
                          value={timelineEntry.id}
                          className="border rounded-lg bg-card shadow-sm md:hover:shadow-lg md:hover:scale-[1.01] dark:md:hover:bg-muted/50 md:hover:border-primary/20 transition-all duration-200 ease-in-out group overflow-hidden"
                        >
                          <div className={accordionRowClassName}>
                            <AccordionHeader className="min-w-0 flex-1">
                              <AccordionTrigger className="hover:no-underline !p-4">
                                <div className="flex min-w-0 flex-1 flex-col text-left md:flex-row md:items-center">
                                  <span className="text-left text-base font-semibold text-foreground">
                                    {timelineEntry.title || "Bez naziva"}
                                  </span>
                                  {timelineEntry.vrijeme && (
                                    <span className="mt-1 text-left text-lg font-semibold text-muted-foreground md:ml-2 md:mt-0">
                                      {timelineEntry.vrijeme}
                                      {timelineEntry.vrijemeEnd &&
                                        ` - ${timelineEntry.vrijemeEnd}`}
                                    </span>
                                  )}
                                </div>
                              </AccordionTrigger>
                            </AccordionHeader>
                            <div className={accordionRowActionsClassName}>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 hover:bg-muted"
                                onClick={() =>
                                  handleEditTimelineEntry(timelineEntry)
                                }
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 text-destructive hover:bg-destructive/10"
                                onClick={() =>
                                  setDeletingTimelineEntryId(timelineEntry.id)
                                }
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          <AccordionContent className="!px-5 !pb-5">
                            <div className="space-y-5 pt-2">
                              <div className="space-y-1.5 pb-3 border-b">
                                <Label
                                  className="text-sm font-medium text-muted-foreground"
                                  style={{ fontSize: "14px" }}
                                >
                                  Vrijeme
                                </Label>
                                <p className="text-sm font-medium text-foreground">
                                  {timelineEntry.vrijeme
                                    ? timelineEntry.vrijemeEnd
                                      ? `${timelineEntry.vrijeme} - ${timelineEntry.vrijemeEnd}`
                                      : timelineEntry.vrijeme
                                    : "-"}
                                </p>
                              </div>
                              <div className="space-y-1.5">
                                <Label
                                  className="text-sm font-medium text-muted-foreground"
                                  style={{ fontSize: "14px" }}
                                >
                                  Dodatne informacije
                                </Label>
                                <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                                  {timelineEntry.dodatneInformacije || "-"}
                                </div>
                              </div>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                    <div className="flex justify-center pt-3">
                      <Button
                        onClick={handleAddTimelineEntry}
                        variant="default"
                        className="w-10 h-10 p-0"
                      >
                        <Plus className="h-5 w-5" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="rounded-xl border border-dashed border-muted-foreground/20 bg-muted/25 px-5 py-10 text-center sm:px-8">
                      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/15 dark:text-white">
                        <CalendarClock className="h-6 w-6" strokeWidth={1.75} />
                      </div>
                      <p className="text-sm font-semibold tracking-tight text-foreground">
                        Nema dodatih događaja u rasporedu
                      </p>
                      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
                        {rasporedEmptyHint}
                      </p>
                    </div>
                    <div className="flex justify-center pt-2">
                      <Button
                        onClick={handleAddTimelineEntry}
                        className="w-10 h-10"
                      >
                        <Plus className="h-5 w-5" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Edit Entry Dialog */}
      <Dialog
        open={!!editingEntryId}
        onOpenChange={(open) =>
          !open && (setEditingEntryId(null), setEditingEntryData(null))
        }
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingEntryData &&
              entries.find((e) => e.id === editingEntryData.id)
                ? "Izmjeni"
                : "Dodaj"}
            </DialogTitle>
            <DialogDescription>Unesite informacije</DialogDescription>
          </DialogHeader>
          {editingEntryData && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="entry-title">Naziv stavke*</Label>
                <Input
                  id="entry-title"
                  value={editingEntryData.title}
                  onChange={(e) =>
                    setEditingEntryData({
                      ...editingEntryData,
                      title: e.target.value,
                    })
                  }
                  placeholder="npr. fotograf, dekoracija, itd.. "
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="entry-naziv">Naziv</Label>
                <Input
                  id="entry-naziv"
                  value={editingEntryData.naziv}
                  onChange={(e) =>
                    setEditingEntryData({
                      ...editingEntryData,
                      naziv: e.target.value,
                    })
                  }
                  placeholder="Unesite naziv"
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="entry-kontakt">Kontakt (opciono)</Label>
                <Input
                  id="entry-kontakt"
                  value={editingEntryData.kontakt}
                  onChange={(e) =>
                    setEditingEntryData({
                      ...editingEntryData,
                      kontakt: e.target.value,
                    })
                  }
                  placeholder="Unesite kontakt (telefon, email, itd.)"
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="entry-cijena">Cijena (opciono)</Label>
                <Input
                  id="entry-cijena"
                  value={editingEntryData.cijena}
                  onChange={(e) =>
                    setEditingEntryData({
                      ...editingEntryData,
                      cijena: e.target.value,
                    })
                  }
                  placeholder="Unesite cijenu"
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="entry-dodatne">
                  Dodatne informacije (opciono)
                </Label>
                <Textarea
                  id="entry-dodatne"
                  value={editingEntryData.dodatneInformacije}
                  onChange={(e) =>
                    setEditingEntryData({
                      ...editingEntryData,
                      dodatneInformacije: e.target.value,
                    })
                  }
                  placeholder="Unesite dodatne informacije"
                  className="mt-2 min-h-32"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditingEntryId(null);
                setEditingEntryData(null);
              }}
            >
              Otkaži
            </Button>
            <Button
              onClick={handleSaveEntry}
              disabled={!editingEntryData?.title.trim()}
            >
              Sačuvaj
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Entry Alert Dialog */}
      <AlertDialog
        open={!!deletingEntryId}
        onOpenChange={(open) => !open && setDeletingEntryId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Potvrdite brisanje</AlertDialogTitle>
            <AlertDialogDescription>
              Da li ste sigurni da želite da obrišete "
              {entries.find((e) => e.id === deletingEntryId)?.title || ""}"? Ova
              akcija se ne može poništiti.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingEntryId(null)}>
              Otkaži
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEntry}
              className="h-10 bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Obriši
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Timeline Entry Dialog */}
      <Dialog
        open={!!editingTimelineEntryId}
        onOpenChange={(open) =>
          !open &&
          (setEditingTimelineEntryId(null), setEditingTimelineEntryData(null))
        }
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTimelineEntryData &&
              timelineEntries.find((e) => e.id === editingTimelineEntryData.id)
                ? "Izmjeni"
                : "Dodaj"}
            </DialogTitle>
            <DialogDescription>Unesite informacije</DialogDescription>
          </DialogHeader>
          {editingTimelineEntryData && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="timeline-entry-title">Naziv stavke*</Label>
                <Input
                  id="timeline-entry-title"
                  value={editingTimelineEntryData.title}
                  onChange={(e) =>
                    setEditingTimelineEntryData({
                      ...editingTimelineEntryData,
                      title: e.target.value,
                    })
                  }
                  placeholder="npr. dolazak gostiju, vjenčanje, večera, itd.. "
                  className="mt-2"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Label
                      htmlFor="time-range-switch"
                      className="text-sm font-normal cursor-pointer m-0"
                    >
                      Vremenski interval
                    </Label>
                    <Switch
                      id="time-range-switch"
                      checked={
                        editingTimelineEntryData.vrijemeEnd !== undefined
                      }
                      onCheckedChange={(checked) =>
                        setEditingTimelineEntryData({
                          ...editingTimelineEntryData,
                          vrijemeEnd: checked ? "" : undefined,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-4">
                  <div>
                    <Label
                      htmlFor="timeline-entry-vrijeme"
                      className="text-sm text-muted-foreground"
                    >
                      {editingTimelineEntryData.vrijemeEnd !== undefined
                        ? "Početak"
                        : "Vrijeme"}
                    </Label>
                    <Input
                      id="timeline-entry-vrijeme"
                      type="time"
                      value={editingTimelineEntryData.vrijeme}
                      onChange={(e) =>
                        setEditingTimelineEntryData({
                          ...editingTimelineEntryData,
                          vrijeme: e.target.value,
                        })
                      }
                      className="mt-2"
                    />
                  </div>
                  {editingTimelineEntryData.vrijemeEnd !== undefined && (
                    <div>
                      <Label
                        htmlFor="timeline-entry-vrijeme-end"
                        className="text-sm text-muted-foreground"
                      >
                        Kraj
                      </Label>
                      <Input
                        id="timeline-entry-vrijeme-end"
                        type="time"
                        value={editingTimelineEntryData.vrijemeEnd || ""}
                        onChange={(e) =>
                          setEditingTimelineEntryData({
                            ...editingTimelineEntryData,
                            vrijemeEnd: e.target.value,
                          })
                        }
                        className="mt-2"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div>
                <Label htmlFor="timeline-entry-dodatne">
                  Dodatne informacije (opciono)
                </Label>
                <Textarea
                  id="timeline-entry-dodatne"
                  value={editingTimelineEntryData.dodatneInformacije}
                  onChange={(e) =>
                    setEditingTimelineEntryData({
                      ...editingTimelineEntryData,
                      dodatneInformacije: e.target.value,
                    })
                  }
                  placeholder="Unesite dodatne informacije"
                  className="mt-2 min-h-32"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditingTimelineEntryId(null);
                setEditingTimelineEntryData(null);
              }}
            >
              Otkaži
            </Button>
            <Button
              onClick={handleSaveTimelineEntry}
              disabled={!editingTimelineEntryData?.title.trim()}
            >
              Sačuvaj
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Timeline Entry Alert Dialog */}
      <AlertDialog
        open={!!deletingTimelineEntryId}
        onOpenChange={(open) => !open && setDeletingTimelineEntryId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Potvrdite brisanje</AlertDialogTitle>
            <AlertDialogDescription>
              Da li ste sigurni da želite da obrišete "
              {timelineEntries.find((e) => e.id === deletingTimelineEntryId)
                ?.title || ""}
              "? Ova akcija se ne može poništiti.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingTimelineEntryId(null)}>
              Otkaži
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTimelineEntry}
              className="h-10 bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Obriši
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Planner events: type → name */}
      <Dialog
        open={isEventTypeDialogOpen}
        onOpenChange={(open) => {
          setIsEventTypeDialogOpen(open);
          if (!open) setSelectedEventType(null);
        }}
      >
        <DialogContent className="max-h-[min(90dvh,42rem)] gap-0 overflow-hidden border-border/60 p-0 shadow-2xl sm:max-w-lg [&>div]:flex [&>div]:max-h-[inherit] [&>div]:min-h-0 [&>div]:flex-col [&>div]:gap-0 [&>div]:overflow-hidden">
          <DialogHeader
            className={cn(
              plannerDialogHeaderClassName,
              "relative shrink-0 space-y-0 text-left",
            )}
          >
            <div className="absolute right-0 top-0 h-32 w-32 translate-x-1/3 -translate-y-1/3 rounded-full bg-primary/[0.09] blur-2xl" />
            <div className="relative flex flex-col items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary shadow-inner ring-1 ring-primary/15 dark:text-white">
                <Sparkles className="h-6 w-6" strokeWidth={1.75} />
              </div>
              <div className="min-w-0 space-y-1.5 pt-0.5 pb-3 text-center">
                <DialogTitle className="text-xl font-bold tracking-tight">
                  Izaberite tip događaja
                </DialogTitle>
              </div>
            </div>
          </DialogHeader>
          <div className="grid min-h-0 flex-1 gap-2.5 overflow-y-auto overscroll-contain px-4 pb-[max(2rem,env(safe-area-inset-bottom,0px)+1rem)] pt-3 sm:px-5 sm:pb-10">
            {EVENT_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              return (
                <button
                  key={opt.type}
                  type="button"
                  className="group flex items-center gap-4 rounded-2xl border border-border/70 bg-card/40 p-4 text-left shadow-sm transition-all hover:border-primary/30 hover:bg-primary/[0.04] hover:shadow-md active:scale-[0.99]"
                  onClick={() => startEventCreation(opt.type)}
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-transparent text-primary ring-1 ring-primary/10 transition-transform group-hover:scale-105 dark:text-white">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 pt-0.5">
                    <span className="block font-semibold leading-snug">
                      {opt.label}
                    </span>
                    
                  </span>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isEventNameDialogOpen}
        onOpenChange={(open) => {
          setIsEventNameDialogOpen(open);
          if (!open) {
            setSelectedEventType(null);
            setNewEventName("");
          }
        }}
      >
        <DialogContent className="gap-0 overflow-hidden border-border/60 p-0 shadow-2xl sm:max-w-md">
          <DialogHeader
            className={cn(
              plannerDialogHeaderClassName,
              "space-y-0 text-left",
            )}
          >
            <div className="flex gap-4 flex-col items-center">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary shadow-inner ring-1 ring-primary/15 dark:text-white">
                <Heart className="h-6 w-6" strokeWidth={1.75} />
              </div>
              <div className="min-w-0 space-y-1.5 pt-0.5 pb-3 text-center">
                <DialogTitle className="text-xl font-bold tracking-tight">
                  Naziv događaja
                </DialogTitle>
              </div>
            </div>
          </DialogHeader>
          <div className="space-y-2 px-5 pb-2 pt-1 sm:px-6">
            <Label htmlFor="new-event-name" className="text-sm font-medium">
              Naziv
            </Label>
            <Input
              id="new-event-name"
              value={newEventName}
              onChange={(e) => setNewEventName(e.target.value)}
              placeholder="npr. Naša svadba"
              className="h-11 border-border/80 bg-background/80 text-base shadow-inner"
              onKeyDown={(e) => {
                if (e.key === "Enter" && newEventName.trim())
                  void saveNewEvent();
              }}
            />
          </div>
          <DialogFooter className="gap-2 border-t border-border/60 bg-muted/20 px-6 py-4 md:pb-0 sm:justify-end">
            <Button
              variant="outline"
              className="min-w-[5.5rem]"
              onClick={() => {
                setIsEventNameDialogOpen(false);
                setSelectedEventType(null);
                setNewEventName("");
              }}
            >
              Otkaži
            </Button>
            <Button
              className="min-w-[5.5rem] font-semibold shadow-md"
              onClick={() => void saveNewEvent()}
            >
              Sačuvaj
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isMyEventsDialogOpen}
        onOpenChange={setIsMyEventsDialogOpen}
      >
        <DialogContent className="gap-0 overflow-hidden border-border/60 p-0 shadow-2xl sm:max-w-md">
          <DialogHeader
            className={cn(
              plannerDialogHeaderClassName,
              "flex flex-col items-center text-center",
            )}
          >
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary shadow-inner ring-1 ring-primary/15 dark:text-white">
                <CalendarDays className="h-4 w-4" />
              </div>
              <div className="space-y-1.5">
                <DialogTitle className="text-xl font-bold text-center tracking-tight">
                  Moje proslave
                </DialogTitle>
                <DialogDescription className="text-sm leading-relaxed">
                  Izaberite proslavu ili napravite novu.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="max-h-[min(50vh,24rem)] space-y-3 overflow-y-auto overscroll-contain px-4 pb-6 pt-2 sm:px-5">
            {plannerSummaries.map((ev) => {
              const typeLabel =
                EVENT_OPTIONS.find((o) => o.type === ev.eventType)?.label ??
                ev.eventType;
              const isActive = activeEventId === ev.id;
              return (
                <Card
                  key={ev.id}
                  className={`transition-colors py-0 ${
                    isActive
                      ? "border-primary/50 bg-primary/[0.06] ring-2 ring-primary/20 dark:bg-primary/10"
                      : ""
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <button
                        type="button"
                        className="min-w-0 flex-1 text-left"
                        onClick={() => switchActiveEvent(ev.id)}
                      >
                        <span className="flex items-center gap-2.5">
                          {isActive && (
                            <span
                              className="inline-flex h-2 w-2 shrink-0 rounded-full bg-primary ring-2 ring-primary/40 ring-offset-2 ring-offset-background"
                              aria-hidden
                            />
                          )}
                          <span className="min-w-0 flex-1 truncate font-medium">
                            {ev.name}
                          </span>
                        </span>
                        <span className="mt-1 block text-xs text-muted-foreground">
                          {typeLabel}
                        </span>
                      </button>
                      <div className="flex shrink-0 items-center gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-10 w-10 p-0 hover:bg-black/5 dark:hover:bg-white/10 dark:hover:text-accent-foreground"
                          aria-label={`Promijeni naziv: ${ev.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            openRenameEventDialog(ev.id);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-10 w-10 p-0 text-destructive hover:text-destructive hover:bg-destructive/10 dark:hover:bg-destructive/20"
                          aria-label={`Obriši proslavu: ${ev.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirmEventId(ev.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <DialogFooter className="flex-col gap-2 border-t border-border/60 bg-muted/20 px-6 py-4 md:pb-0">
            <Button
              variant="default"
              className="w-full"
              onClick={() => {
                setIsMyEventsDialogOpen(false);
                setIsEventTypeDialogOpen(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Nova proslava
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isRenameEventDialogOpen}
        onOpenChange={(open) => {
          setIsRenameEventDialogOpen(open);
          if (!open) {
            setRenameEventId(null);
            setRenameEventName("");
          }
        }}
      >
        <DialogContent className="gap-0 overflow-hidden border-border/60 p-0 shadow-2xl sm:max-w-md">
          <DialogHeader
            className={cn(
              plannerDialogHeaderClassName,
              "flex flex-col items-center text-center",
            )}
          >
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary shadow-inner ring-1 ring-primary/15 dark:text-white">
                <Edit className="h-4 w-4" />
              </div>
              <div className="space-y-1.5 text-center">
                <DialogTitle className="text-xl font-bold tracking-tight">
                  Naziv proslave
                </DialogTitle>
                <DialogDescription className="text-sm leading-relaxed">
                  Unesite novi naziv. Vaši podaci ostaju vezani za ovu proslavu.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="space-y-2 px-5 pb-2 pt-1 sm:px-6">
            <Label htmlFor="rename-event-name" className="text-sm font-medium">
              Naziv
            </Label>
            <Input
              id="rename-event-name"
              value={renameEventName}
              onChange={(e) => setRenameEventName(e.target.value)}
              placeholder="npr. Naša svadba"
              disabled={renameEventSaving}
              className="h-11 border-border/80 bg-background/80 text-base shadow-inner"
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  renameEventName.trim() &&
                  !renameEventSaving
                ) {
                  void saveRenamedEvent();
                }
              }}
            />
          </div>
          <DialogFooter className="gap-2 border-t border-border/60 bg-muted/20 px-6 py-4 md:pb-0 sm:justify-center">
            <Button
              variant="outline"
              className="min-w-[5.5rem]"
              disabled={renameEventSaving}
              onClick={() => {
                setIsRenameEventDialogOpen(false);
                setRenameEventId(null);
                setRenameEventName("");
              }}
            >
              Otkaži
            </Button>
            <Button
              className="min-w-[5.5rem] font-semibold shadow-md"
              disabled={renameEventSaving || !renameEventName.trim()}
              onClick={() => void saveRenamedEvent()}
            >
              Sačuvaj
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteConfirmEventId}
        onOpenChange={(open) => {
          if (!open && !deleteEventLoading) setDeleteConfirmEventId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Obrisati proslavu?</AlertDialogTitle>
            <AlertDialogDescription>
              Ova radnja se ne može poništiti. Proslava{" "}
              <span className="font-medium text-foreground">
                „
                {deleteConfirmEventId
                  ? plannerSummaries.find((e) => e.id === deleteConfirmEventId)
                      ?.name ?? ""
                  : ""}
                “
              </span>{" "}
              će biti uklonjena. Povezani podaci na serveru (gosti, budžet,
              zadaci) mogu sprečiti brisanje dok su vezani za ovu proslavu.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteEventLoading}>
              Otkaži
            </AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={deleteEventLoading}
              className="h-10"
              onClick={() => void confirmDeleteEvent()}
            >
              {deleteEventLoading ? "Brisanje…" : "Obriši"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
